import { Grid2, Paper, Stack, TextField } from "@mui/material";
import { observer } from "mobx-react";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { SwayyProductDetailStore } from "../../../stores/SwayyProductDetailStore";
import { swayyColors, swayyRadii } from "../../../theme/tokens";

interface SwayyProductSeoPanelProps {
  store: SwayyProductDetailStore;
  disabled?: boolean;
  onCancel: () => void;
  onSave: () => void;
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: swayyRadii.sm,
    backgroundColor: swayyColors.white,
    "& fieldset": { borderColor: swayyColors.border },
    "&:hover fieldset": { borderColor: swayyColors.pine },
    "&.Mui-focused fieldset": { borderColor: swayyColors.pine },
  },
  "& input, & textarea": {
    fontSize: 14,
    color: swayyColors.deepGreen,
  },
} as const;

const primaryBtnSx = {
  minHeight: 34,
  px: 1.75,
  fontSize: 13,
  color: swayyColors.white,
  backgroundColor: swayyColors.pine,
  "&:hover": {
    backgroundColor: swayyColors.pineHover,
    color: swayyColors.white,
  },
  "&.Mui-disabled": {
    color: swayyColors.white,
    opacity: 0.55,
  },
} as const;

const FieldLabel = ({ label, hint }: { label: string; hint?: string }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ mb: 0.75, minHeight: 18 }}
  >
    <CustomTypography
      variant="caption"
      color={swayyColors.textSecondary}
      sx={{ fontWeight: 600, lineHeight: 1.2 }}
    >
      {label}
    </CustomTypography>
    {hint ? (
      <CustomTypography variant="caption" color={swayyColors.textSecondary}>
        {hint}
      </CustomTypography>
    ) : null}
  </Stack>
);

/**
 * SEO tab — FE-only surface for fields already on the product
 * (slug + description). Saves via existing product update API.
 */
const SwayyProductSeoPanel = ({
  store,
  disabled = false,
  onCancel,
  onSave,
}: SwayyProductSeoPanelProps) => {
  const busy = disabled || store.isSaving;

  return (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: { xs: 2, md: 2.5 },
        borderRadius: swayyRadii.lg,
        borderColor: swayyColors.border,
        backgroundColor: swayyColors.white,
      }}
    >
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <CustomTypography
            variant="heading5"
            color={swayyColors.deepGreen}
            sx={{ fontWeight: 700 }}
          >
            SEO
          </CustomTypography>
          <CustomTypography variant="body2" color={swayyColors.textSecondary}>
            Slug and description already saved on this product. Edit them here
            for discovery; no separate SEO fields yet.
          </CustomTypography>
        </Stack>

        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 8 }}>
            <FieldLabel
              label="Slug"
              hint="URL path for the product page"
            />
            <TextField
              size="small"
              fullWidth
              value={store.form.slug}
              disabled={busy}
              onChange={(event) =>
                store.updateForm("slug", event.target.value)
              }
              placeholder="product-slug"
              sx={fieldSx}
            />
          </Grid2>
          <Grid2 size={12}>
            <FieldLabel
              label="Description"
              hint="Product description used on the storefront"
            />
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={4}
              value={store.form.description}
              disabled={busy}
              onChange={(event) =>
                store.updateForm("description", event.target.value)
              }
              placeholder="Describe the product…"
              sx={fieldSx}
            />
          </Grid2>
        </Grid2>

        <Stack
          direction="row"
          spacing={1}
          justifyContent="flex-end"
          sx={{ pt: 0.5 }}
        >
          <AppButton
            label="Cancel"
            variant="secondary"
            disabled={busy || !store.hasChanges}
            onClick={onCancel}
          />
          <AppButton
            label={store.isSaving ? "Saving…" : "Save"}
            variant="primary"
            disabled={busy || !store.hasChanges}
            onClick={onSave}
            customStyles={primaryBtnSx}
          />
        </Stack>
      </Stack>
    </Paper>
  );
};

export default observer(SwayyProductSeoPanel);
